import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useChatsDeps } from './ChatsDeps'
import { ChatList } from './ChatList'
import type { ChatRow } from './chatRows'
import { useSwipeBack } from './phone/useSwipeBack'
import { usePinnedChatScreen } from './phone/usePinnedChatScreen'
import { Icon } from './Icon'
import { clearActiveChat, readActiveChat, readListWidth, saveActiveChat, saveListWidth } from './storage'
import { ListDivider } from './ListDivider'
import { ScheduledPanel } from './ScheduledPanel'
import { StatsPanel } from './StatsPanel'

type View = 'stats' | 'scheduled'

/** What the open chat can ask of the page. */
export interface OpenChatActions {
  /** The chat went away (hidden, cleared): close it and forget it as the last chat. */
  leave: () => void
  /** Phones: back to the list (the chat's header draws the button); null on a desktop. */
  back: (() => void) | null
  /** Open another chat of this Account (as the list does). */
  open: (fanId: string, fanName: string) => void
  /** The list's rows on screen (phones: the avatar rail and the Back badge). */
  siblings: ChatRow[]
  /** Phones: mark the chat unread on the list and go back to it (OnlyFans was told by the caller). */
  markUnread: (() => void) | null
}

interface Props {
  accountId: string
  /** The open chat (the console passes the React chat window). */
  renderChat: (fanId: string, fanName: string, chat: OpenChatActions) => ReactNode
  /** Unread messages on the loaded chats, for the rail badge. */
  onUnread?: (count: number) => void
  /** Phones show the list until a chat is picked; desktop reopens the last chat. */
  narrow?: boolean
  /** Phones: the CRM tab bar, under the list (hidden while a chat is open). */
  mobileNav?: ReactNode
  /** May read Statistics: the mass-message Stats tool. */
  canReadStats?: boolean
}

/**
 * The Chats page (ADR 0024): one OnlyFans Account's chats list and the open
 * chat. The open fan lives in the URL (`?fan=`), and the last one is remembered
 * per account. On a desktop it is replaced, so Back leaves the page; on a
 * phone, opening a chat from the list is a step of its own, so Back (the
 * browser's, or the system's swipe) returns to the list (ADR 0035).
 */
export function ChatsPage({ accountId, renderChat, onUnread, narrow = false, mobileNav, canReadStats = false }: Props) {
  const { t } = useTranslation('chats')
  const { storageScope } = useChatsDeps()
  const [params, setParams] = useSearchParams()
  const fanId = params.get('fan')?.trim() || null
  const rawView = params.get('view')
  const view: View | null = rawView === 'scheduled' || (rawView === 'stats' && canReadStats) ? rawView : null
  const [names, setNames] = useState<Record<string, string>>({})
  const [rows, setRows] = useState<ChatRow[]>([])
  const [leftUnread, setLeftUnread] = useState<string | null>(null)
  const [listWidth, setListWidth] = useState(readListWidth)

  const navigate = useNavigate()
  // Phones: whether the open chat sits on a history entry of its own (opened
  // from the list here), so leaving it is a step back rather than a new entry.
  const chatPushed = useRef(false)
  const onList = useRef(!fanId && !view)
  onList.current = !fanId && !view
  useEffect(() => {
    if (!fanId) chatPushed.current = false
  }, [fanId])

  const open = useCallback(
    (id: string, name: string) => {
      setNames((prev) => (prev[id] === name ? prev : { ...prev, [id]: name }))
      setLeftUnread(null)
      const push = narrow && onList.current
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('fan', id)
          next.delete('view')
          return next
        },
        { replace: !push },
      )
      if (push) chatPushed.current = true
    },
    [setParams, narrow],
  )

  // Scheduled / Stats take the chat's place (as on the CRM page); Back
  // returns to the chat that was open when the panel opened.
  const beforeView = useRef<string | null>(null)
  const showView = useCallback(
    (v: View | null) => {
      if (v && fanId) beforeView.current = fanId
      const back = v ? null : beforeView.current
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('fan')
          if (back) next.set('fan', back)
          if (v) next.set('view', v)
          else next.delete('view')
          return next
        },
        { replace: true },
      )
    },
    [setParams, fanId],
  )

  const backToList = useCallback(() => {
    if (chatPushed.current) {
      chatPushed.current = false
      navigate(-1)
      return
    }
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('fan')
        next.delete('view')
        return next
      },
      { replace: true },
    )
  }, [setParams, navigate])

  const leave = useCallback(() => {
    clearActiveChat(storageScope)
    backToList()
  }, [storageScope, backToList])

  const markUnread = useCallback(() => {
    if (!fanId) return
    setLeftUnread(fanId)
    backToList()
  }, [fanId, backToList])

  // Phones: drag the open chat right to go back; the list peeks in underneath.
  const swipe = useSwipeBack(narrow && fanId && !view ? backToList : null)
  const peek = swipe.dx > 0 || swipe.leaving
  usePinnedChatScreen(narrow && !!fanId && !view)

  // No fan in the URL: the desktop reopens the chat the person last had open here.
  const restored = useRef(false)
  useEffect(() => {
    if (restored.current || fanId || view || narrow) return
    restored.current = true
    const last = readActiveChat(storageScope)
    if (last) open(last.fanId, last.fanName)
  }, [fanId, view, narrow, storageScope, open])

  useEffect(() => {
    if (fanId) saveActiveChat(storageScope, fanId, names[fanId] ?? '')
  }, [fanId, names, storageScope])

  return (
    <div
      className={`cp-page ${fanId || view ? 'has-chat' : ''}`}
      style={{ '--crm-chat-list-width': `${listWidth}px` } as React.CSSProperties}
    >
      {/* Phones: the list stays mounted (scroll, live state) but hides under an open chat. */}
      <aside
        className={`cp-list ${peek ? 'is-peek' : ''}`}
        hidden={narrow && Boolean(fanId || view) && !peek}
        aria-hidden={peek || undefined}
        inert={peek || undefined}
        style={peek ? ({ '--cp-peek-x': `${swipe.leaving ? 0 : Math.min(0, -90 + swipe.dx * 0.25)}px` } as React.CSSProperties) : undefined}
      >
        <ChatList
          accountId={accountId}
          activeFanId={fanId}
          onOpen={open}
          onUnread={onUnread}
          onRows={setRows}
          leftUnread={leftUnread}
          phone={
            narrow
              ? {
                  massHref: `/account/${encodeURIComponent(accountId)}/crm/mass`,
                  onScheduled: () => showView('scheduled'),
                  onStats: canReadStats ? () => showView('stats') : undefined,
                }
              : undefined
          }
          tools={
            <>
              <button
                type="button"
                className="chats-panel-tool"
                aria-label={t('tools.scheduled')}
                title={t('tools.scheduled')}
                aria-pressed={view === 'scheduled'}
                onClick={() => showView(view === 'scheduled' ? null : 'scheduled')}
              >
                <Icon id="icon-schedule" size={20} />
              </button>
              {canReadStats && (
                <button
                  type="button"
                  className="chats-panel-tool"
                  aria-label={t('tools.stats')}
                  title={t('tools.stats')}
                  aria-pressed={view === 'stats'}
                  onClick={() => showView(view === 'stats' ? null : 'stats')}
                >
                  <Icon id="icon-statements-lg" size={20} />
                </button>
              )}
              <Link className="chats-panel-tool" to={`/account/${encodeURIComponent(accountId)}/crm/mass`} aria-label={t('tools.newMass')} title={t('tools.newMass')}>
                <Icon id="icon-add" size={20} />
              </Link>
            </>
          }
        />
        {narrow && !fanId && mobileNav}
      </aside>
      {!narrow && <ListDivider width={listWidth} onChange={setListWidth} onCommit={saveListWidth} />}
      <section
        className={`cp-chat ${swipe.dragging ? 'is-dragging' : ''} ${swipe.leaving ? 'is-leaving' : ''}`}
        hidden={narrow && !fanId && !view}
        style={swipe.dx && !swipe.leaving ? ({ '--cp-chat-x': `${swipe.dx}px` } as React.CSSProperties) : undefined}
        {...swipe.handlers}
      >
        {narrow && view && (
          <div className="cp-back-bar">
            <button type="button" className="chats-panel-tool" aria-label={t('backToChats')} title={t('backToChats')} onClick={backToList}>
              <Icon id="icon-back" size={18} />
            </button>
          </div>
        )}
        {view === 'stats' ? (
          <StatsPanel onClose={() => showView(null)} />
        ) : view === 'scheduled' ? (
          <ScheduledPanel onClose={() => showView(null)} />
        ) : fanId ? (
          renderChat(fanId, names[fanId] ?? '', { leave, back: narrow ? backToList : null, open, siblings: rows, markUnread: narrow ? markUnread : null })
        ) : (
          <div className="cp-empty">{t('pickChat')}</div>
        )}
      </section>
    </div>
  )
}
